<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import type { ChartOptions, Color, TooltipItem } from 'chart.js';
  import BarChart from '@/views/details/components/influencer-insights/BarChart.vue';

  const props = defineProps<{
    data: {
      male: number[];
      female: number[];
    };
  }>();

  const barChartRef = ref<any>(null);

  const isMobile = ref(window.innerWidth <= 768);

  const updateIsMobile = () => {
    isMobile.value = window.innerWidth <= 768;
  };

  // Add event listener for window resize
  window.addEventListener('resize', updateIsMobile);

  // Clean up event listener when component unmounts
  onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile);
  });

  const genderData = computed(() => ({
    labels: ['18-24', '25-34', '35-44', '45+'],
    datasets: [
      {
        label: 'Male',
        data: props.data.male,
        backgroundColor: '#4C6EF5', // Blue
        hoverOffset: 4,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: isMobile.value ? 10 : 25,
        maxBarThickness: isMobile.value ? 30 : 50,
      },
      {
        label: 'Female',
        data: props.data.female,
        backgroundColor: '#74C0FC', // Light Blue
        hoverOffset: 4,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: isMobile.value ? 10 : 25,
        maxBarThickness: isMobile.value ? 30 : 50,
      },
    ],
  }));

  const doughnutChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false, // Set to false if you want the tooltip to appear even if not directly intersecting a bar
    },
    elements: {
      arc: {
        borderWidth: 10,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#333',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          padding: 24,
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        backgroundColor: '#1E1B39',
        yAlign: 'bottom',
        padding: {
          top: 10,
          right: 30,
          bottom: 10,
          left: 10,
        },
        bodySpacing: 4,
        cornerRadius: 8,
        callbacks: {
          title: () => {
            return '';
          },
          label: tooltipItem => {
            return `  ${tooltipItem.formattedValue}`;
          },
          labelColor: function (tooltipItem: TooltipItem<'bar'>) {
            const backgroundColor = tooltipItem.dataset.backgroundColor;
            let color: Color = '#000';

            if (backgroundColor) {
              if (Array.isArray(backgroundColor)) {
                color = backgroundColor[tooltipItem.dataIndex] as Color;
              } else {
                color = backgroundColor as Color;
              }
            }

            return {
              borderColor: color,
              backgroundColor: color,
              borderWidth: 3,
              borderRadius: 3,
            };
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
        },
      },
      y: {
        ticks: {
          color: '#69747C',

          callback: function (val) {
            return `${this.getLabelForValue(val as number)}`;
          },
        },
        grid: {
          color: '#F1F1F1',
        },
        // max: barChartMaxLimit.value,
        border: { display: false, dash: [4, 4] },
      },
    },
  };

  let resizeObserver: ResizeObserver | null = null;

  const hasData = computed(() => {
    return !!(props.data.male.length || props.data.female.length);
  });

  onMounted(() => {
    // Handle chart resize when container size changes
    const chartContainer = barChartRef.value?.$el?.parentElement;
    if (chartContainer) {
      resizeObserver = new ResizeObserver(() => {
        if (barChartRef.value?.chart) {
          barChartRef.value.chart.resize();
        }
      });
      resizeObserver.observe(chartContainer);
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
</script>

<template>
  <div class="report-age-chart-container flex flex-col gap-24">
    <h2 class="chart-heading">Audience by Age</h2>
    <div class="chart-wrapper">
      <BarChart v-if="hasData" ref="barChartRef" :data="genderData" :options="doughnutChartOptions" />
      <div v-else class="no-data-state">
        <p>No age data available</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #64748b;
  }

  .no-data-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 250px;
    color: #64748b;
    font-size: 16px;

    @media screen and (min-width: 768px) {
      min-height: 300px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 350px;
    }
  }

  .report-age-chart-container {
    background-color: white;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    display: flex;
    flex-direction: column;
    height: 100%;

    .chart-heading {
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;

      @media screen and (min-width: 768px) {
        font-size: 24px;
      }
    }
  }

  .chart-wrapper {
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media screen and (min-width: 768px) {
      min-height: 300px;
    }

    @media screen and (min-width: 1024px) {
      min-height: 350px;
    }

    canvas {
      max-height: 100%;
      max-width: 100%;
    }
  }
</style>
